/**
 * @module components/Toast
 * @description Toast notification system using react-toastify.
 * Provides consistent toast notifications with success, error, and info variants.
 */

import React from 'react';
import { ToastContainer, toast, type ToastOptions } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastDuration, ToastHostProps, ToastType } from './types';

/**
 * Default toast options used throughout the application to keep behavior consistent.
 */
const defaultOptions: ToastOptions = {
  position: 'bottom-right',
  autoClose: ToastDuration.DEFAULT,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'dark',
};

/**
 * Host component that renders the global ToastContainer once.
 */
export const ToastHost: React.FC<ToastHostProps> = ({ className }) => {
  return <ToastContainer className={className} {...defaultOptions} />;
};

/**
 * Internal utility to dispatch a toast with merged defaults.
 */
const notify = (message: string, options?: ToastOptions, type?: ToastType) => {
  const base: ToastOptions = { ...defaultOptions, ...options };
  if (type === ToastType.ERROR) {
    // Errors should stay visible until dismissed unless explicitly overridden.
    base.autoClose = options?.autoClose ?? false;
  }
  if (type) {
    return toast(message, { ...base, type });
  }
  return toast(message, base);
};

/**
 * Convenience helpers for common toast types.
 */
export const Toast = {
  /** Show a success toast that auto-dismisses. */
  success: (message: string, options?: ToastOptions) => notify(message, options, ToastType.SUCCESS),
  /** Show an error toast that stays until dismissed. */
  error: (message: string, options?: ToastOptions) => notify(message, options, ToastType.ERROR),
  /** Show an informational toast. */
  info: (message: string, options?: ToastOptions) => notify(message, options, ToastType.INFO),
};

export default ToastHost;
